import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { adminApi } from "../../../api/resources";
import type { InvitationAdmin } from "../../../api/types";
import { Modal, SelectFilter } from "../../../components/Overlay";
import { Button, DemoBadge, StatusPill } from "../../../components/ui";
import { INVITATION_STATUS_LABEL, fmtDate } from "../../../utils/labels";
import { DataTable, LoadState, useLoad } from "../shared";

const OPTIONS: [string, string][] = [["", "All statuses"], ...Object.entries(INVITATION_STATUS_LABEL), ["EXPIRING_48H", "Pending – expiring within 48h"]];

interface Props {
  status: string;
  onStatus: (s: string) => void;
  reloadKey: number;
  onChanged: (url?: string | null) => void;
}

export function InvitationsTab({ status, onStatus, reloadKey, onChanged }: Props) {
  const loader = useCallback(() => adminApi.invitations(status || undefined), [status, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<InvitationAdmin[]>(loader);
  const [revoking, setRevoking] = useState<InvitationAdmin | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const resend = async (inv: InvitationAdmin) => {
    setBusy(inv.id);
    try {
      const res = await adminApi.resendInvitation(inv.id);
      toast.success(`Invitation re-sent to ${inv.email}. The previous link no longer works.`);
      onChanged(res.registration_url);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    try {
      await adminApi.revokeInvitation(revoking.id);
      toast.success("Invitation revoked");
      setRevoking(null);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div data-testid="invitations-tab">
      <div className="mb-6 flex justify-end">
        <SelectFilter value={status} onChange={onStatus} options={OPTIONS} testId="invitations-status-filter" label="Invitation status filter" />
      </div>
      <LoadState error={error} loading={!data} testId="invitations-load-error" />
      {data && (
        <DataTable testId="invitations-table" headers={["Name", "Email", "Status", "Expires", "Sends (24h)", ""]} empty={data.length === 0}>
          {data.map((i) => (
            <tr key={i.id} data-testid={`invitation-row-${i.id}`}>
              <td className="px-5 py-4 text-white"><span className="mr-2">{i.full_name}</span><DemoBadge show={i.is_demo} testId={`invitation-demo-badge-${i.id}`} /></td>
              <td className="px-5 py-4 text-zinc-300">{i.email}</td>
              <td className="px-5 py-4"><StatusPill status={i.status} label={INVITATION_STATUS_LABEL[i.status]} testId={`invitation-status-${i.id}`} /></td>
              <td className="px-5 py-4 text-xs text-zinc-400" data-testid={`invitation-expires-${i.id}`}>{fmtDate(i.expires_at)}</td>
              <td className="px-5 py-4 text-xs text-zinc-400">{i.sends_last_24h}/5</td>
              <td className="px-5 py-4 text-right">
                {(i.status === "PENDING" || i.status === "EXPIRED") && (
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" className="h-8 px-3 text-[11px]" loading={busy === i.id} onClick={() => resend(i)} data-testid={`invitation-resend-${i.id}`}>Resend</Button>
                    <Button variant="ghost" className="h-8 px-3 text-[11px] hover:text-signal" onClick={() => setRevoking(i)} data-testid={`invitation-revoke-${i.id}`}>Revoke</Button>
                  </div>
                )}
                {i.status === "REJECTED" && <span className="text-xs text-zinc-500" title={i.rejection_reason ?? ""}>Rejected: {i.rejection_reason}</span>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      <Modal open={!!revoking} onClose={() => setRevoking(null)} title="Revoke invitation?" testId="revoke-confirm-modal">
        <p className="text-sm text-zinc-300">The link sent to <span className="text-white">{revoking?.email}</span> will stop working immediately.</p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setRevoking(null)} data-testid="revoke-cancel-button">Cancel</Button>
          <Button onClick={revoke} data-testid="revoke-confirm-button">Revoke</Button>
        </div>
      </Modal>
    </div>
  );
}
